import { Inject, Injectable, Optional } from '@angular/core';
import * as L from 'leaflet';
import { Layer, LatLngBounds } from 'leaflet';
import '@ngageoint/leaflet-geopackage';
import { GeoPackageAPI } from '@ngageoint/geopackage';
import georaster from 'georaster';
import GeoRasterLayer from 'georaster-layer-for-leaflet';
import { PROJECT_PROVIDER, GEODATA_API_SERVICE } from '../../core/tokens';
import { ProjectProvider, IGeodataApiService } from '../../core/interfaces';
import { MapStatus, LayerDetails, OptionalLayer } from '../types';
import { LayerService, getWmsBasicAuthHeader } from './layer.service';
import { OfflineLayerService } from './offline-layer.service';
import { MapStatusService } from './map-status.service';
import { GpkgRenderingService } from './gpkg-rendering.service';
import { TransformationService } from './transformation.service';
import { OfflineGridLayer, OfflineGridLayerOptions } from '../layers/offline-grid-layer';
import { GLOBAL_OSM_STORE } from './global-tile-seed.service';
import { StorageService } from '../../services/storage/storage.service';
import { OfflineModeService } from '../../services/offline-mode.service';
import { MBTilesService } from './mbtiles.service';
import { DgmCalibrationService } from './dgm-calibration.service';
import { firstValueFrom, filter } from 'rxjs';
import { HttpEventType, HttpResponse } from '@angular/common/http';

/**
 * Bridge interface for MapLayerFactoryService to access MapComponent state.
 */
export interface LayerFactoryBridge {
  getMapStatus(): MapStatus | undefined;
}

/**
 * Service für Layer-Erstellung: Tile-, WMS-, GeoPackage-, GeoTiff- und GeoJSON-Layer.
 * Extrahiert aus MapComponent (Plan 15 Phase 4).
 */
@Injectable({ providedIn: 'root' })
export class MapLayerFactoryService {
  private map!: L.Map;
  private bridge!: LayerFactoryBridge;

  constructor(
    private ls: LayerService,
    @Inject(PROJECT_PROVIDER) private ps: ProjectProvider,
    private oms: OfflineModeService,
    private os: OfflineLayerService,
    private storage: StorageService,
    private mapStatusService: MapStatusService,
    private gpkgRenderingService: GpkgRenderingService,
    private ts: TransformationService,
    @Optional() @Inject(GEODATA_API_SERVICE) private geodataApi: IGeodataApiService | null,
    private mbtilesService: MBTilesService,
    private dgmCalibration: DgmCalibrationService,
  ) {}

  init(map: L.Map, bridge: LayerFactoryBridge): void {
    this.map = map;
    this.bridge = bridge;
  }

  destroy(): void {
    this.map = undefined as any;
    this.bridge = undefined as any;
  }

  makeTileLayer(url: string, options: L.TileLayerOptions, name: string, func: (l: Layer) => void) {
    // Cache-Lookup nur wenn der gecachte Layer zum aktuellen Modus und Projekt passt
    // (OfflineGridLayer mit onlineUrl für Online, ohne für Offline)
    const cachedLayer = this.ls.getCachedLayer(name);
    if (cachedLayer) {
      const cachedOpts = (cachedLayer as any).options as OfflineGridLayerOptions | undefined;
      const cachedHasOnlineUrl = !!cachedOpts?.onlineUrl;
      const needsOnlineUrl = !this.oms.offlineMode();
      const cachedProject = cachedOpts?.project || '';
      const currentProject = this.ps.project() || '';
      // Nur verwenden wenn Online-URL-Konfiguration UND Projekt übereinstimmen
      if (cachedHasOnlineUrl === needsOnlineUrl && cachedProject === currentProject) {
        func(cachedLayer);
        return;
      }
    }

    const optasany = options as any;
    optasany.name = name;
    optasany.crossOrigin = true;

    // Pane-Name für diesen TileLayer berechnen und erstellen
    const paneName = `layer-${name.replace(/[^a-zA-Z0-9]/g, '_')}`;
    if (this.map && !this.map.getPane(paneName)) {
      const pane = this.map.createPane(paneName);
      pane.style.zIndex = '450';
    }
    optasany.pane = paneName;

    // MBTiles für z7-11 (Basiskarte), Luzula-Server für z12+ (beide Modi)
    const currentProject = this.ps.project() || '';
    if (this.oms.offlineMode()) {
      this.os.hasImages(name).then((maxZoom: false|number) => {
        const gridLayer = new OfflineGridLayer({
          name: name,
          project: currentProject,
          maxNativeZoom: maxZoom || 18,
          mbtiles: this.mbtilesService,
          mbtilesMaxZoom: 11,
          noWrap: true,
          minZoom: 7,
          maxZoom: 18,
          pane: paneName,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        }, this.storage);
        func(gridLayer);
      });
    } else {
      const gridLayer = new OfflineGridLayer({
        name: name,
        project: currentProject,
        mbtiles: this.mbtilesService,
        mbtilesMaxZoom: 11,
        onlineUrl: 'https://osm.luzula.de/styles/osm-luzula/256/{z}/{x}/{y}.png',
        noWrap: true,
        minZoom: 7,
        maxZoom: 18,
        pane: paneName,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      }, this.storage);
      func(gridLayer);
    }
  }

  makeWMSTileLayers(ld: LayerDetails, options: L.WMSOptions, func: (l: L.Layer, layerName?: string) => void, hiddenLayers?: Set<string>) {
    const isDgmUrl = (ld.url || '').toLowerCase();
    const isDgm = isDgmUrl.includes('dgm_wms') || isDgmUrl.includes('dgm/service');
    const dgmCalibrated = isDgm && !!this.dgmCalibration.settings();

    // DGM: Sublayer-Liste normalisieren je nach Kalibrierungsstatus
    // Egal was in der mapDef steht — hier entscheiden wir welche WMS-Layer tatsächlich geladen werden
    let effectiveLayers = ld.layers;
    if (isDgm && effectiveLayers?.some(l => l.enabled)) {
      const hasAnyDgmEnabled = effectiveLayers.some(l =>
        l.enabled && (l.name.includes('hoehendarstellung') || l.name.includes('schummerung'))
      );
      if (hasAnyDgmEnabled) {
        // Immer hoehendarstellung_schummerung verwenden (hat Schummerung eingebaut).
        // Bei Kalibrierung wird die Pixel-Transformation darauf angewendet.
        const displayTitle = effectiveLayers.find(l => l.enabled && l.name.includes('hoehendarstellung'))?.title || 'DGM Höhenfarben + Schummerung';
        effectiveLayers = [
          { name: 'hoehendarstellung_schummerung', title: displayTitle, enabled: true, transparent: true },
        ];
      }
    }

    effectiveLayers?.forEach((layer: OptionalLayer) => {
      if (!layer.enabled) return;

      const layerName = layer.title || layer.name;

      // Versteckte Layer nicht erstellen (spart Bandbreite)
      // Bei Einblenden wird layersChanged getriggert und Layer wird erstellt
      if (hiddenLayers?.has(layerName)) {
        // func mit null aufrufen, aber layerName übergeben damit maybeDone zählen kann
        func(null as any, layerName);
        return;
      }

      // Pane-Name für diesen WMS-Sublayer berechnen
      const paneName = `layer-${layerName.replace(/[^a-zA-Z0-9]/g, '_')}`;

      // Pane erstellen falls noch nicht vorhanden
      if (this.map && !this.map.getPane(paneName)) {
        const pane = this.map.createPane(paneName);
        pane.style.zIndex = '450';
      }

      const currentProject = this.ps.project();

      // Im Offline-Modus heruntergeladene Tiles verwenden
      if (this.oms.offlineMode()) {
        this.os.hasImages(layerName).then((maxZoom: false|number) => {
          // Bounds aus Projektregionen berechnen, Fallback auf mapStatus.bounds
          const projectRegions = this.ls.regions()?.regions ?? [];
          let bounds: LatLngBounds | undefined;
          if (projectRegions.length > 0) {
            const regionBounds = projectRegions.map(r => r.bounds);
            bounds = regionBounds.reduce((acc, b: any) => {
              const lb = new LatLngBounds(b._southWest, b._northEast);
              return acc ? acc.extend(lb) : lb;
            }, undefined as LatLngBounds | undefined);
          } else if (this.bridge.getMapStatus()?.bounds) {
            const b = this.bridge.getMapStatus()!.bounds as any;
            bounds = new LatLngBounds(b._southWest, b._northEast);
          }
          // DGM-Kalibrierung auch im Offline-Modus
          const offlinePixelTransform = (isDgm && dgmCalibrated && layer.name === 'hoehendarstellung_schummerung')
            ? this.dgmCalibration.createPixelTransform() : null;

          // OfflineGridLayer für Offline-Modus (IndexedDB-Tiles)
          const gridLayer = new OfflineGridLayer({
            name: layerName,
            project: currentProject,
            maxNativeZoom: maxZoom || 18,
            noWrap: true,
            bounds: bounds,
            pane: paneName,
            pixelTransform: offlinePixelTransform || undefined,
          }, this.storage);
          this.attachTileLoadingEvents(gridLayer);
          func(gridLayer, layerName);
        });
      } else {
        // Online-Modus: OfflineGridLayer mit WMS-URL (nutzt Memory-Cache)
        // tileSize 1024 für weniger Requests bei WMS-Layern (16x weniger als 256)

        // Auth-Header aus WMS-Server-Konfiguration holen (falls vorhanden)
        const wmsServer = this.ls.wmsServerMap.get(ld.wmsServerName || '');
        const authHeader = wmsServer ? getWmsBasicAuthHeader(wmsServer) : null;

        // Kleinere tileSize (256) wenn minZoom gesetzt ist (z.B. DenkmalGIS mit Scale-Limits)
        const tileSize = wmsServer?.minZoom ? 256 : 1024;

        // DGM: Immer hoehendarstellung_schummerung, bei Kalibrierung mit pixelTransform
        let wmsLayerName = layer.name;
        let pixelTransform: ((imageData: ImageData) => void) | null = null;

        if (isDgm && dgmCalibrated && layer.name === 'hoehendarstellung_schummerung') {
          pixelTransform = this.dgmCalibration.createPixelTransform();
        }

        const gridLayer = new OfflineGridLayer({
          name: layerName,
          project: currentProject || '',
          wmsUrl: ld.url,
          wmsLayers: wmsLayerName,
          wmsFormat: (options as any).format || 'image/png',
          wmsTransparent: ld.transparent !== false,
          wmsCrs: wmsServer?.crs,
          maxNativeZoom: ld.maxNativeZoom,
          minZoom: wmsServer?.minZoom,
          noWrap: true,
          pane: paneName,
          tileSize: tileSize,
          authHeader: authHeader || undefined,
          pixelTransform: pixelTransform || undefined,
        }, this.storage);
        this.attachTileLoadingEvents(gridLayer);
        func(gridLayer, layerName);
      }
    });
  }

  /**
   * Registriert Events für Tile-Loading-Indikator auf einem GridLayer.
   * Erhöht/Verringert tilesLoading-Signal im MapStatusService wenn Layer lädt/fertig ist.
   */
  attachTileLoadingEvents(layer: L.GridLayer): void {
    layer.on('loading', () => {
      this.mapStatusService.tilesLoading.update(v => v + 1);
    });
    layer.on('load', () => {
      this.mapStatusService.tilesLoading.update(v => Math.max(0, v - 1));
    });
  }

  async makeGeoPackageTileLayer(name: string, layer: string): Promise<Layer | undefined> {
    const layerName = layer.substring(0, layer.lastIndexOf('('));
    const geoPackageBuffer = await this.ps.getProjectValue(name, 'attachments') as ArrayBuffer;
    if (!geoPackageBuffer) return;
    const geoPackage = await GeoPackageAPI.open(Buffer.from(geoPackageBuffer));
    const l = (L as any).geoPackageTileLayer({
      geoPackage: geoPackage,
      layerName: layerName,
      sqlJsWasmLocateFile: (filename: string) => `/assets/leaflet/${filename}`
    });
    return l;
  }

  async makeGeoPackageLayers(ld: LayerDetails, func: (l: L.Layer, name?: string) => void, onMissing?: (name: string) => void) {
    // Hilfsfunktion: onMissing für alle aktivierten Sublayer aufrufen
    const callOnMissingForAll = () => {
      ld.layers?.forEach((layer: OptionalLayer) => {
        if (!layer.enabled) return;
        if (onMissing) onMissing(`${ld.name}_${layer.name}`);
      });
    };

    let geoPackageBuffer: ArrayBuffer | undefined;
    try {
      // Timeout für getProjectValue (5 Sekunden)
      const getPromise = this.ps.getProjectValue(ld.name, 'attachments') as Promise<ArrayBuffer | undefined>;
      const timeoutPromise = new Promise<undefined>((resolve) =>
        setTimeout(() => resolve(undefined), 5000)
      );
      geoPackageBuffer = await Promise.race([getPromise, timeoutPromise]);
    } catch (error) {
      console.error(`Fehler beim Laden von GeoPackage ${ld.name} aus Storage:`, error);
      callOnMissingForAll();
      return;
    }

    if (!geoPackageBuffer) {
      // Keine Datei vorhanden - trotzdem maybeDone für jeden Sublayer aufrufen
      console.warn(`GeoPackage ${ld.name} nicht gefunden oder Timeout`);
      callOnMissingForAll();
      return;
    }

    let geoPackage: any;
    try {
      // Timeout für GeoPackage-Open (10 Sekunden)
      const openPromise = GeoPackageAPI.open(Buffer.from(geoPackageBuffer));
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('GeoPackage open timeout')), 10000)
      );
      geoPackage = await Promise.race([openPromise, timeoutPromise]);
    } catch (error) {
      console.error(`Fehler beim Öffnen von GeoPackage ${ld.name}:`, error);
      callOnMissingForAll();
      return;
    }
    ld.layers?.forEach((layer: OptionalLayer) => {
      if (!layer.enabled) return;

      const fullLayerName = `${ld.name}_${layer.name}`;

      try {
        if (layer.type === 'tile') {
          const geoTileLayer = (L as any).geoPackageTileLayer({
            geoPackage: geoPackage,
            layerName: layer.name,
            sqlJsWasmLocateFile: (filename: string) => `/assets/leaflet/${filename}`
          });
          if (geoTileLayer) {
            func(geoTileLayer, fullLayerName);
          } else {
            console.warn(`GeoPackage Tile-Layer ${layer.name} konnte nicht erstellt werden`);
            if (onMissing) onMissing(fullLayerName);
          }
          return;
        }

        // Feature-Layer: Style laden
        const features: any[] = [];
        let gpkgStyle: 'standort' | 'humusform' | 'haken' | null = null;
        let styleApplied = false;

        // Gespeicherten Style laden
        const storageKey = `gpkgStyle_${ld.name}_${layer.name}`;
        const savedStyle = this.storage.getSync(storageKey);
        let layerStyle: any = savedStyle || { color: '#666', weight: 2, fillColor: 'transparent', fillOpacity: 0 };

        const geoLayer = (L as any).geoPackageFeatureLayer([], {
          geoPackage: geoPackage,
          layerName: layer.name,
          style: () => ({
            color: layerStyle.color,
            weight: layerStyle.weight,
            opacity: 1,
            fillColor: layerStyle.fillColor,
            fillOpacity: layerStyle.fillOpacity,
          }),
          interactive: false,
          onEachFeature: (feature: any, featureLayer: L.Layer) => {
            features.push(feature);

            // Beim ersten Feature: Style erkennen und auf alle Features anwenden
            if (!styleApplied && !savedStyle) {
              gpkgStyle = this.gpkgRenderingService.detectGpkgStyleFromFeature(feature);
              if (gpkgStyle === 'standort') {
                layerStyle = { color: '#cc0000', weight: 2, fillColor: 'transparent', fillOpacity: 0 };
              } else if (gpkgStyle === 'humusform') {
                layerStyle = { color: '#228B22', weight: 2, fillColor: 'transparent', fillOpacity: 0 };
              }
              styleApplied = true;

              // Style sofort auf den Layer anwenden (für alle bisherigen und kommenden Features)
              setTimeout(() => {
                geoLayer?.setStyle({
                  color: layerStyle.color,
                  weight: layerStyle.weight,
                  opacity: 1,
                  fillColor: layerStyle.fillColor,
                  fillOpacity: layerStyle.fillOpacity,
                });
              }, 0);
            }
          },
          sqlJsWasmLocateFile: (filename: string) => `/assets/leaflet/${filename}`
        });

        if (geoLayer) {
          // Labels NACH Feature-Laden erstellen
          setTimeout(() => {
            if (features.length > 0) {
              // Style-Typ für Labels ermitteln (falls nicht schon erkannt)
              const detectedStyle = gpkgStyle || this.gpkgRenderingService.detectGpkgStyleFromFeature(features[0]);

              // Haken-Layer: Spezielle Marker-Darstellung
              if (detectedStyle === 'haken') {
                const hakenLayer = this.gpkgRenderingService.createHakenMarkerLayer(features, fullLayerName);
                if (hakenLayer) {
                  // Original-Layer unsichtbar machen
                  geoLayer.setStyle({ opacity: 0, fillOpacity: 0 });
                  // Haken-Layer speichern für Sichtbarkeitssteuerung
                  this.gpkgRenderingService.gpkgHakenLayers.set(fullLayerName, hakenLayer);
                  this.map.addLayer(hakenLayer);
                }
              } else {
                // Zoom-abhängige Liniendicke für alle anderen Layer
                const baseWeight = layerStyle.weight || 2;
                let lastZoom = -1;
                const updateLineWeight = () => {
                  const zoom = Math.round(this.map.getZoom());
                  // Nur updaten wenn Zoomstufe sich geändert hat
                  if (zoom === lastZoom) return;
                  lastZoom = zoom;

                  let weight: number;
                  if (zoom >= 16) {
                    weight = baseWeight * 1.5;
                  } else if (zoom <= 12) {
                    weight = Math.max(0.3, baseWeight * 0.2);
                  } else {
                    const scale = 0.2 + ((zoom - 12) / 4) * 0.8;
                    weight = Math.max(0.3, baseWeight * scale);
                  }
                  geoLayer.setStyle({ weight });
                };
                this.map.on('zoomend', updateLineWeight);
                updateLineWeight();

                // Labels erstellen (nicht für Haken)
                if (detectedStyle) {
                  const labelData = this.gpkgRenderingService.collectGpkgLabelData(features, detectedStyle);
                  if (labelData.length > 0) {
                    const canvasLabelLayer = this.gpkgRenderingService.createCanvasLabelLayer(labelData, detectedStyle);
                    this.gpkgRenderingService.gpkgLabelLayers.set(fullLayerName, canvasLabelLayer);
                    this.gpkgRenderingService.setupGpkgZoomHandler(fullLayerName, geoLayer, canvasLabelLayer);
                  }
                }
              }
            }
          }, 500);

          func(geoLayer, fullLayerName);
        } else {
          // geoLayer konnte nicht erstellt werden
          console.warn(`GeoPackage Feature-Layer ${layer.name} konnte nicht erstellt werden`);
          if (onMissing) onMissing(fullLayerName);
        }
      } catch (error) {
        console.error(`Fehler beim Erstellen von GeoPackage-Layer ${layer.name}:`, error);
        if (onMissing) onMissing(fullLayerName);
      }
    });
  }

  async makeGeoTiffRasterLayer(name: string, resolution?: number): Promise<Layer | undefined> {
    try {
      const georasterBuffer = await this.ps.getProjectValue(name, 'attachments') as ArrayBuffer;
      if (!georasterBuffer) {
        console.warn(`GeoTiff buffer not found for ${name}`);
        return undefined;
      }
      console.log(`GeoTiff buffer size for ${name}: ${georasterBuffer.byteLength} bytes`);
      const raster = await georaster(georasterBuffer);
      console.log(`GeoRaster created for ${name}`);
      const l = new GeoRasterLayer({
        georaster: raster,
        opacity: 0.5,
        resolution: resolution ?? 256
      });
      return l;
    } catch (e) {
      console.error(`Error creating GeoTiff layer for ${name}:`, e);
      return undefined;
    }
  }

  async makeGeoJsonLayer(name: string, geojsonStyle?: any): Promise<L.GeoJSON | undefined> {
    let geojsonContent = await this.ps.getProjectValue(name, 'attachments') as string;

    // Fallback: vom Server laden wenn lokal nicht vorhanden
    if (!geojsonContent && this.geodataApi && this.oms.networkOnline()) {
      try {
        const projectId = this.ps.project();
        console.log(`[GeoJSON] Lade ${name} vom Server...`);
        const response = await firstValueFrom(
          this.geodataApi.download(projectId, name).pipe(
            filter(e => e.type === HttpEventType.Response)
          )
        );
        const buf = (response as HttpResponse<ArrayBuffer>).body;
        if (buf) {
          geojsonContent = new TextDecoder().decode(buf);
          // Lokal speichern für nächstes Mal
          await this.ps.setProjectValue(name, geojsonContent, 'attachments');
          await this.ps.addGeodataFile(name);
          console.log(`[GeoJSON] ${name} vom Server geladen und lokal gespeichert`);
          this.ls.attachmentsChanged.emit();
        }
      } catch (err) {
        console.warn(`[GeoJSON] Server-Download fehlgeschlagen für ${name}:`, err);
      }
    }

    if (!geojsonContent) {
      console.warn(`GeoJSON content not found for ${name}`);
      return undefined;
    }

    try {
      const geojson = JSON.parse(geojsonContent);

      // CRS aus GeoJSON lesen (falls vorhanden)
      const crsName = geojson.crs?.properties?.name;
      const needsTransform = crsName && !crsName.includes('4326') && !crsName.includes('WGS');

      // Pane für diesen GeoJSON-Layer erstellen (gleiche Namenskonvention wie andere Layer)
      const paneName = `layer-${name.replace(/[^a-zA-Z0-9]/g, '_')}`;
      if (this.map && !this.map.getPane(paneName)) {
        const pane = this.map.createPane(paneName);
        pane.style.zIndex = '450';  // Standard-Z-Index, wird von onLayerOrderChanged angepasst
      }

      // Gespeicherten Style laden oder Standard-Style verwenden
      const storageKey = `geojsonStyle_${name}`;
      let savedStyle = this.storage.getSync(storageKey);
      if (!savedStyle && geojsonStyle) {
        savedStyle = geojsonStyle;
        this.storage.setSync(storageKey, geojsonStyle);
      }
      if (!savedStyle) {
        savedStyle = { color: 'darkslategray', weight: 2, fillColor: 'transparent', fillOpacity: 0 };
      }

      const options: L.GeoJSONOptions = {
        pane: paneName,
        style: {
          color: savedStyle.color || 'darkslategray',
          weight: savedStyle.weight || 2,
          opacity: 0.8,
          fill: savedStyle.fillOpacity > 0,
          fillColor: savedStyle.fillColor || 'transparent',
          fillOpacity: savedStyle.fillOpacity || 0,
          interactive: false
        }
      };

      // Labels anzeigen wenn labelProperty konfiguriert
      if (savedStyle.labelProperty) {
        const labelProp = savedStyle.labelProperty;
        options.onEachFeature = (feature: any, layer: L.Layer) => {
          const label = feature.properties?.[labelProp];
          if (label) {
            (layer as any).bindTooltip(String(label), {
              permanent: true,
              direction: 'center',
              className: 'geojson-label',
              pane: paneName,
            });
          }
        };
      }

      // Koordinaten transformieren wenn nicht WGS84
      if (needsTransform) {
        // EPSG-Code aus CRS-Name extrahieren (z.B. "urn:ogc:def:crs:EPSG::5650" -> "EPSG:5650")
        const epsgMatch = crsName.match(/EPSG[:]*(\d+)/i);
        const epsgCode = epsgMatch ? `EPSG:${epsgMatch[1]}` : 'EPSG:5650';
        console.log(`GeoJSON CRS: ${crsName} -> ${epsgCode}, transforming coordinates`);

        options.coordsToLatLng = (coords: [number, number, number?]) => {
          // coords ist [x, y] oder [x, y, z]
          const transformed = this.ts.from({ system: epsgCode as any, x: coords[0], y: coords[1] });
          return transformed;
        };
      }

      const layer = L.geoJSON(geojson, options);

      if (this.map) {
        const pane = this.map.getPane(paneName);

        // Zoom-abhängige Polygon-Invertierung (Betriebsgrenze positiv ↔ negativ)
        if (savedStyle.invertAtMinZoom != null && pane) {
          const invertZoom = savedStyle.invertAtMinZoom;
          const invertedGeojson = this._invertGeoJson(geojson);
          const invertLayer = L.geoJSON(invertedGeojson, {
            pane: paneName,
            style: {
              color: savedStyle.invertColor || savedStyle.color || '#cc0000',
              weight: savedStyle.invertWeight || 2,
              opacity: 0.8,
              fill: true,
              fillColor: savedStyle.invertFillColor || '#ffffff',
              fillOpacity: savedStyle.invertFillOpacity || 0.6,
              interactive: false,
            }
          });

          const baseWeight = savedStyle.weight || 4;
          const updateInvert = () => {
            const zoom = this.map!.getZoom();
            if (zoom >= invertZoom) {
              // Invertiert: weiße Fläche außerhalb
              if (this.map!.hasLayer(layer)) this.map!.removeLayer(layer);
              if (!this.map!.hasLayer(invertLayer)) invertLayer.addTo(this.map!);
            } else {
              // Positiv: rot gefüllt, Rand wird beim Herauszoomen dicker
              if (this.map!.hasLayer(invertLayer)) this.map!.removeLayer(invertLayer);
              if (!this.map!.hasLayer(layer)) layer.addTo(this.map!);
              const scaledWeight = Math.round(baseWeight * Math.pow(1.5, 14 - zoom));
              layer.setStyle({ weight: Math.min(scaledWeight, 25) });
            }
          };
          this.map.on('zoomend', updateInvert);
          // Initial: Layer wird von map.component hinzugefügt, also nur invertLayer managen
          setTimeout(() => updateInvert(), 100);
        }

        // Zoom-abhängige Sichtbarkeit: Layer entfernen/hinzufügen (nicht nur Pane, damit Tooltips auch verschwinden)
        const zoomMin = savedStyle.minZoom;
        const zoomMax = savedStyle.maxZoom;
        if (zoomMin != null || zoomMax != null) {
          const updateVisibility = () => {
            const zoom = this.map!.getZoom();
            const visible = (zoomMin == null || zoom >= zoomMin)
                         && (zoomMax == null || zoom <= zoomMax);
            if (visible && !this.map!.hasLayer(layer)) {
              layer.addTo(this.map!);
            } else if (!visible && this.map!.hasLayer(layer)) {
              this.map!.removeLayer(layer);
            }
          };
          this.map.on('zoomend', updateVisibility);
          updateVisibility();
        }
      }

      return layer;
    } catch (e) {
      console.error('GeoJSON-Parsing fehlgeschlagen:', e);
      return undefined;
    }
  }

  /**
   * Invertiert ein GeoJSON: BBox-Rechteck mit Polygonen als Löchern.
   */
  private _invertGeoJson(geojson: any): any {
    const polygons = (geojson.features || []).filter(
      (f: any) => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon'
    );
    if (polygons.length === 0) return geojson;

    // BBox aller Features
    let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
    for (const f of polygons) {
      const coords = f.geometry.type === 'Polygon'
        ? f.geometry.coordinates[0]
        : f.geometry.coordinates.flatMap((p: any) => p[0]);
      for (const [lng, lat] of coords) {
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }
    }
    const dLng = (maxLng - minLng) || 0.01;
    const dLat = (maxLat - minLat) || 0.01;
    const outerRing = [
      [minLng - dLng * 2, minLat - dLat * 2],
      [maxLng + dLng * 2, minLat - dLat * 2],
      [maxLng + dLng * 2, maxLat + dLat * 2],
      [minLng - dLng * 2, maxLat + dLat * 2],
      [minLng - dLng * 2, minLat - dLat * 2],
    ];

    // Alle äußeren Ringe als Löcher
    const holes: any[] = [];
    for (const f of polygons) {
      if (f.geometry.type === 'Polygon') {
        holes.push(f.geometry.coordinates[0].slice().reverse());
      } else {
        for (const poly of f.geometry.coordinates) {
          holes.push(poly[0].slice().reverse());
        }
      }
    }

    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: [outerRing, ...holes] },
      }],
    };
  }
}
